import { SearchIcon } from "lucide-react";
import React from "react";
import { useTitle } from "react-use";

import { trpc } from "@blis/trpc/react";

import { CommandMenu, CommandMenuProvider } from "@/components/CommandMenu";
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateHeading,
  EmptyStateIcon,
} from "@/components/ui/empty-state";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { useInitializeNavStack } from "@/utils/useNavStack";
import { RecentSearches } from "../components/RecentSearches";
import { SearchInput } from "../components/SearchInput";
import { SearchList } from "../components/SearchList";
import { AccessionSearchContext } from "../components/SearchProvider";
import { useSearchHistory, useSearchQuery } from "../store";

export function SearchPage() {
  useTitle("Search");

  const [queryStr] = useSearchQuery();
  const scrollRef = React.useRef<HTMLDivElement>(null);

  const { data, isLoading } = trpc.accession.search.useQuery(
    { search: queryStr },
    {
      enabled: !!queryStr,
    },
  );

  const history = useSearchHistory();

  useInitializeNavStack([queryStr]);

  return (
    <>
      <AccessionSearchContext.Provider
        value={{ isLoading, search: queryStr, items: data || [] }}
      >
        <div className="flex items-center h-12 px-4 border-b shrink-0">
          <SidebarTrigger className="mr-1 inline-flex md:hidden" />
          <SearchInput />
        </div>
        <div
          className="w-full flex-1 overflow-auto"
          ref={scrollRef}
          data-scroll-restoration-id="search-list"
        >
          {isLoading || data?.length ? (
            <SearchList scrollRef={scrollRef} />
          ) : queryStr ? (
            <EmptyState>
              <EmptyStateIcon>
                <SearchIcon />
              </EmptyStateIcon>
              <EmptyStateHeading>No results</EmptyStateHeading>
              <EmptyStateDescription>
                No results found for "{queryStr}"
              </EmptyStateDescription>
            </EmptyState>
          ) : history.length > 0 ? (
            <RecentSearches />
          ) : null}
        </div>
      </AccessionSearchContext.Provider>
      <CommandMenuProvider>
        <CommandMenu />
      </CommandMenuProvider>
    </>
  );
}
